import {
  AuthLocalStorage,
  getProfilesFromSessionStorage,
  getToken,
  Profile,
} from 'applicaiton/sessionStorage/auth';
import axios from 'axios';
import { usersRefreshToken } from 'applicaiton/store/reducers/Users/ActionCreators';
import dayjs from 'dayjs';
import { store } from 'applicaiton/store';
import { ApplicationType, x_app_type } from '../../applicaiton';

/**
 * Create an Axios instance with a base URL and response type set to blob.
 */
const axiosBlobInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  responseType: 'blob',
});

// Add a request interceptor
axiosBlobInstance.interceptors.request.use(
  async function (config) {
    const profile: Profile[] = getProfilesFromSessionStorage()!;
    const date = localStorage.getItem(AuthLocalStorage.accessTokenExpiresAt);
    /**
     * Subtract one minute from the access token expiration date and convert it to ISO string format.
     */
    const minusMinuteDate = date
      ? dayjs(date).subtract(59, 'minute').toISOString()
      : null;
    /**
     * Check if the current date is valid and if the access token has expired.
     */
    const isDateValid =
      dayjs().isValid() && dayjs(minusMinuteDate).diff(dayjs(), 'minute') <= 59;
    /**
     * Check if the access token has expired.
     */
    const isExpiredToken = minusMinuteDate
      ? dayjs().isAfter(minusMinuteDate)
      : false;

    if (!isDateValid || isExpiredToken) {
      if (!profile?.length) return;

      /**
       * Dispatch a usersRefreshToken action to refresh the token and get the new access token.
       */
      const data = await store.dispatch(
        usersRefreshToken({
          refreshToken: profile[0].refreshToken,
          email: profile[0].email,
        }),
      );

      /**
       * If the token was successfully refreshed, update the request headers with the new access token.
       */
      if (data && data.meta.requestStatus === 'fulfilled') {
        return {
          ...config,
          ...{
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-type': 'Application/json',
              Authorization: `Bearer ${getToken()}`,
              [x_app_type]: ApplicationType.ProfessionalWeb,
            },
          },
        };
      }
    }

    /**
     * If the token is still valid, update the request headers with the current access token.
     */
    const token = getToken();
    if (!token) return;

    return {
      ...config,
      ...(token
        ? {
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-type': 'Application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        : {}),
    };
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  },
);

// Add a response interceptor
axiosBlobInstance.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    return Promise.reject(error);
  },
);

export default axiosBlobInstance;
